import { CheckCircle } from 'lucide-react'
import type { Organisation, XeroInvoiceState } from '../../types'

const PAYMENT_HEADERS = [
  'Invoice',
  'Organisation',
  'Amount',
  'Status',
  'Paid On',
  'Entitlement',
] as const

interface RecentPaymentsTableProps {
  xeroInvoices: XeroInvoiceState[]
  organisations: Organisation[]
}

function statusBadgeClass(status: string): string {
  if (status === 'PAID') {
    return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
  }
  if (status === 'AUTHORISED') {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
  }
  return 'bg-stone-100 text-stone-800 dark:bg-stone-700 dark:text-stone-300'
}

interface PaymentRowProps {
  invoice: XeroInvoiceState
  organisations: Organisation[]
}

function PaymentRow({ invoice, organisations }: PaymentRowProps): React.ReactElement {
  const org = organisations.find(
    (organisation) => organisation.xeroOrganisationId === invoice.xeroOrganisationId
  )
  const amountDisplay = `${invoice.currencyCode} ${invoice.amountPaid.toFixed(2)}`
  return (
    <tr className="hover:bg-stone-50 dark:hover:bg-stone-700/30">
      <td className="px-4 py-3 font-medium text-stone-900 dark:text-white">
        {invoice.xeroInvoiceNumber}
      </td>
      <td className="px-4 py-3 text-stone-600 dark:text-stone-300">
        {org?.name ?? <span className="text-stone-400 italic">Unknown</span>}
      </td>
      <td className="px-4 py-3 text-stone-600 dark:text-stone-300">{amountDisplay}</td>
      <td className="px-4 py-3">
        <span
          className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${statusBadgeClass(invoice.invoiceStatus)}`}
        >
          {invoice.invoiceStatus}
        </span>
      </td>
      <td className="px-4 py-3 text-stone-600 dark:text-stone-300">{invoice.fullyPaidOn ?? '-'}</td>
      <td className="px-4 py-3">
        {invoice.entitlementId ? (
          <span className="inline-flex items-center gap-1 text-[#5c6e94] dark:text-[#8a9bba]">
            <CheckCircle className="h-4 w-4" />
            Linked
          </span>
        ) : (
          <span
            className="text-stone-400 dark:text-stone-500"
            title="Link to an entitlement to enable activation"
          >
            —
          </span>
        )}
      </td>
    </tr>
  )
}

function EmptyPaymentsRow(): React.ReactElement {
  return (
    <div className="p-4 text-center text-sm text-stone-500 dark:text-stone-400">
      No payment records found. Use the Import section above to add payments.
    </div>
  )
}

export function RecentPaymentsTable({
  xeroInvoices,
  organisations,
}: RecentPaymentsTableProps): React.ReactElement {
  const recordSuffix = xeroInvoices.length === 1 ? '' : 's'
  return (
    <div className="rounded-xl border border-stone-200 bg-white dark:border-stone-700 dark:bg-stone-800">
      <div className="border-b border-stone-200 px-4 py-3 dark:border-stone-700">
        <h3 className="font-semibold text-stone-900 dark:text-white">Recent Payments</h3>
        <p className="text-xs text-stone-500 dark:text-stone-400">
          {xeroInvoices.length} payment record{recordSuffix}
        </p>
      </div>
      {xeroInvoices.length === 0 ? (
        <EmptyPaymentsRow />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 dark:bg-stone-700/50">
              <tr>
                {PAYMENT_HEADERS.map((header) => (
                  <th
                    key={header}
                    className="px-4 py-2 text-left font-medium text-stone-600 dark:text-stone-300"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-200 dark:divide-stone-700">
              {xeroInvoices.map((invoice) => (
                <PaymentRow key={invoice.id} invoice={invoice} organisations={organisations} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
